import { Signal, computed, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  AuthSession,
  confirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  resendSignUpCode,
  resetPassword,
  sendUserAttributeVerificationCode,
  signIn,
  signInWithRedirect,
  signOut,
  updatePassword,
  updateUserAttribute,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import rbac, { Permission, Role } from '@optical/rbac';
import {
  hasAuthProvider,
  isNotNull,
  isNotUndefined,
  parseRolesFromGroups,
  parseTenantIdFromGroups,
} from '@optical/utils';

export interface UserInfo {
  email: string;
  emailVerified: boolean;
  familyName?: string;
  givenName?: string;
}

/**
 * Where a user stands on the way from "not signed in" to "may use the app".
 *
 * Route-agnostic on purpose: it says what the user still has to do, not where they are. The auth
 * guard owns the stage ⇄ route mapping and is the only place that turns this value into a
 * redirect.
 *
 * There is no "pending email verification" stage: completing a sign-up is workflow state owned by
 * the auth module (see `PendingRegistrationService`), not something the session knows — a user on
 * that path has no session yet and is simply `anonymous` here.
 */
export type AuthStage =
  /** No session. Sign in, sign up, or recover a password. */
  | 'anonymous'
  /** An invited user signed in with a temporary password and must choose a real one. */
  | 'pending-new-password'
  /** A signed-in user who belongs to no organization yet. */
  | 'needs-organization'
  /** Fully set up. */
  | 'ready';

/**
 * Cognito's answer to a sign-in attempt.
 *
 * `confirm-sign-up` (the account exists but its email was never confirmed) is returned to the
 * caller rather than kept as state: what follows is the signup workflow's business, not the
 * session's. The new-password challenge, by contrast, is store state, because it is part of the
 * in-flight sign-in that {@link AuthStore.confirmSignIn} continues.
 */
export type SignInNextStep = 'done' | 'confirm-sign-up' | 'new-password-required';

/**
 * Name of the SSO auth provider. Must match the name of the federated identity provider
 * configured in AWS.
 */
const CUSTOM_AUTH_PROVIDER = 'microsoft-azure-ad';

/**
 * Shape of the initial state of the auth store.
 */
interface AuthState {
  authSession: AuthSession | null;
  /** Whether an in-flight sign-in is waiting on the new-password challenge. */
  hasSignInStep: boolean;
  userInfo: UserInfo | null;
}

/**
 * The initial state of the auth store.
 */
const initialState: AuthState = {
  authSession: null,
  hasSignInStep: false,
  userInfo: null,
};

export type AuthStore = InstanceType<typeof AuthStore>;

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    /**
     * Whether the user is authenticated and has completed the sign-in process.
     */
    isAuthenticated: computed(() => isNotNull(state.authSession()) && !state.hasSignInStep()),
    /**
     * The user's ID token.
     */
    idToken: computed(() => state.authSession()?.tokens?.idToken ?? null),
    /**
     * The ID of the current user.
     */
    userId: computed(() => state.authSession()?.userSub ?? null),
  })),
  withComputed((state) => ({
    /**
     * The user's cognito groups read from the ID token.
     */
    groups: computed(() => (state.idToken()?.payload['cognito:groups'] ?? []) as string[]),
    /**
     * The JWT is used to make authenticated request against the backend.
     */
    jwt: computed(() => state.idToken()?.toString() ?? null),
  })),
  withComputed((state) => ({
    /**
     * The roles of the current user.
     */
    roles: computed(() => {
      const groups = state.groups();

      return parseRolesFromGroups(groups);
    }),
    /**
     * Whether the user is authenticated via SSO.
     */
    sso: computed(() => {
      const groups = state.groups();
      const hasCustomAuthProvider = hasAuthProvider(CUSTOM_AUTH_PROVIDER);

      return hasCustomAuthProvider(groups);
    }),
    /**
     * The ID of the current user's tenant.
     */
    tenantId: computed(() => {
      const groups = state.groups();

      return parseTenantIdFromGroups(groups);
    }),
  })),
  withComputed((state) => ({
    /**
     * Whether the current user is a player user.
     */
    isPlayer: computed(() => {
      const roles = state.roles();

      return roles.includes(Role.PLAYER);
    }),
    /**
     * The current {@link AuthStage}.
     *
     * The order of the checks is the precedence. The new-password challenge outranks the session
     * check: Cognito hands back a challenge *instead of* tokens, so a user part-way through one
     * has no session and would otherwise look anonymous.
     */
    stage: computed<AuthStage>(() => {
      if (state.hasSignInStep()) {
        return 'pending-new-password';
      }

      if (isNotNull(state.authSession())) {
        return state.tenantId() ? 'ready' : 'needs-organization';
      }

      return 'anonymous';
    }),
  })),
  withMethods((state) => ({
    /**
     * Reset the auth store to the initial state.
     */
    reset(): void {
      patchState(state, { ...initialState });
    },
    /**
     * Confirm the reset password step.
     *
     * @param email - The email address of the user.
     * @param confirmationCode - The confirmation code.
     * @param newPassword - The new password.
     */
    async confirmResetPassword(
      email: string,
      confirmationCode: string,
      newPassword: string,
    ): Promise<void> {
      await confirmResetPassword({
        confirmationCode,
        newPassword,
        username: email,
      });
    },
    /**
     * Resend the confirmation code to the user's email address.
     *
     * @param username - The user's email (Cognito username).
     */
    async resendConfirmationCode(username: string): Promise<void> {
      await resendSignUpCode({ username });
    },
    /**
     * Reset the user's password.
     *
     * @param email - The email address of the user.
     * @returns The email address to which the verification code was sent.
     */
    async resetPassword(email: string): Promise<string | undefined> {
      const { nextStep } = await resetPassword({ username: email });

      return nextStep.codeDeliveryDetails.destination;
    },
    /**
     * Send a verification code to the user's email address.
     *
     * @returns The email address to which the verification code was sent.
     */
    async sendEmailVerificationCode(): Promise<string | undefined> {
      const { destination } = await sendUserAttributeVerificationCode({
        userAttributeKey: 'email',
      });

      return destination;
    },
    /**
     * Sign in the user using SSO.
     */
    async signInWithSSO(): Promise<void> {
      await signInWithRedirect({
        provider: {
          custom: CUSTOM_AUTH_PROVIDER,
        },
      });
    },
    /**
     * Sign out the current user.
     *
     * @param global - Whether to sign out globally.
     */
    async signOut(global = false): Promise<void> {
      await signOut({ global });
    },
    /**
     * Update the user's password.
     *
     * @param oldPassword - The user's current password.
     * @param newPassword - The user's new password.
     */
    async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
      await updatePassword({
        newPassword,
        oldPassword,
      });
    },
  })),
  withMethods((state) => ({
    /**
     * Refresh the auth session.
     *
     * @param forceRefresh - Whether to force a refresh even if the session is not expired.
     */
    async refreshAuthSession(forceRefresh = false): Promise<void> {
      try {
        const authSession = await fetchAuthSession({ forceRefresh });
        const hasAccessToken = isNotUndefined(authSession.tokens?.accessToken);

        patchState(state, { authSession: hasAccessToken ? authSession : null });
      } catch (error) {
        console.error('Error refreshing auth session:', error);
        state.signOut();
      }
    },
    async refreshUserInfo(): Promise<void> {
      try {
        const userAttributes = await fetchUserAttributes();
        const userInfo: UserInfo = {
          email: userAttributes.email ?? '',
          emailVerified: userAttributes.email_verified === 'true',
          familyName: userAttributes.family_name,
          givenName: userAttributes.given_name,
        };

        patchState(state, { userInfo });
      } catch (error) {
        console.error('Error refreshing user info:', error);
        state.signOut();
      }
    },
  })),
  withMethods((state) => ({
    /**
     * Confirm the sign-in step with the new password.
     *
     * @param newPassword - The new password.
     */
    async confirmSignIn(newPassword: string): Promise<void> {
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      const hasSignInStep = nextStep.signInStep !== 'DONE';

      patchState(state, { hasSignInStep });

      if (!hasSignInStep) {
        await state.refreshAuthSession();
      }
    },
    /**
     * Sign in the user with the specified email and password.
     *
     * When the sign-in completes, the session is refreshed before this resolves — Amplify hands
     * the tokens to the Hub asynchronously, and a caller that navigated right away would race the
     * auth guard against a store that still looks anonymous.
     *
     * @param email - The user's email.
     * @param password - The user's password.
     * @returns What Cognito wants next.
     */
    async signIn(email: string, password: string): Promise<SignInNextStep> {
      const { nextStep } = await signIn({
        username: email,
        password,
      });

      /** If a self-registration process is not completed, the nextStep will be "CONFIRM_SIGN_UP" */
      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        patchState(state, { hasSignInStep: false });

        return 'confirm-sign-up';
      }

      // If user account was created with a temporary password, the nextStep will be
      // "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      const hasSignInStep = nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

      patchState(state, { hasSignInStep });

      if (!hasSignInStep) {
        await state.refreshAuthSession();
      }

      return hasSignInStep ? 'new-password-required' : 'done';
    },
  })),
  withMethods((state) => ({
    /**
     * Confirm the user's email address.
     *
     * @param confirmationCode - The confirmation code.
     */
    async confirmEmail(confirmationCode: string): Promise<void> {
      await confirmUserAttribute({
        confirmationCode,
        userAttributeKey: 'email',
      });

      await state.refreshUserInfo();
    },
    /**
     * Update the user's email address.
     *
     * The user will need to confirm the new email address by providing the verification code sent
     * to the new email address.
     * See {@link confirmEmail} for more information.
     *
     * @param newEmail - The new email address.
     */
    async updateEmail(newEmail: string): Promise<void> {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'email',
          value: newEmail,
        },
      });

      await state.refreshUserInfo();
    },
    /**
     * Update the user's attributes.
     *
     * @param attributes - The attributes to update.
     */
    async updateUserAttributes(attributes: Record<string, string>): Promise<void> {
      await updateUserAttributes({
        userAttributes: attributes,
      });

      await state.refreshUserInfo();
    },
  })),
  withMethods((state) => ({
    /**
     * Check if the user has the specified permission.
     *
     * @param permission - The permission to check for.
     * @returns A signal that emits whether the user has the specified permission.
     */
    hasPermission(permission: Permission): Signal<boolean> {
      return computed(() => state.roles().some((role) => rbac.hasPermission(role, permission)));
    },
    /**
     * Check if the user has the specified role.
     *
     * @param role - The role to check for.
     * @returns A signal that emits whether the user has the specified role.
     */
    hasRole(role: Role): Signal<boolean> {
      return computed(() => state.roles().includes(role));
    },
  })),
  withProps((state) => ({
    userInfo$: toObservable(state.userInfo),
    removeAuthHubCallback: (): void => void 0,
  })),
  withHooks({
    onInit(store, router = inject(Router)) {
      // Listen for authentication events from the Amplify Hub to respond to sign-in and sign-out events.
      store.removeAuthHubCallback = Hub.listen('auth', ({ payload: { event } }): void => {
        switch (event) {
          case 'signedIn':
            // On successful sign-in, refresh the auth session
            store.refreshAuthSession();

            break;
          case 'signedOut':
            // Reset before navigating: the auth guard decides where the user may go by reading
            // this store, so it must see the signed-out state when it evaluates /login.
            store.reset();
            router.navigate(['/login']);

            break;
        }
      });

      // Refresh the user info when the user's authentication state changes.
      effect(() => {
        if (store.isAuthenticated()) {
          store.refreshUserInfo();
        }
      });
    },
    onDestroy(store) {
      store.removeAuthHubCallback();
    },
  }),
);
